import { useToast } from '@afterpaytouch/core'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'
import { capture } from '@afterpaytouch/integrations/monitoring'
import type { TFunction } from 'next-i18next'
import { sendPurchaseEvent } from '../../integrations/googletagmanager/utils'
import { getConfirmCheckoutErrorHandling } from '../../utils/redirects'
import { getCheckpoint } from '../../model'
import { Route } from '../../config/router'
import {
  resetCheckoutError,
  setCardScanCheckpoint,
  setDisplayProcessingModal,
  setEndConfirmTime,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useCheckoutToken,
  useConfirmCheckoutArgs,
  useHasSavedPaymentMethods,
  useConfirmCheckoutMutation,
} from '..'

type ConfirmCheckoutToastMessageKey = 'summary:complianceDeclineSystemError'

const getConfirmCheckoutToastMessage = (translations: { t: TFunction }, messageKey?: ConfirmCheckoutToastMessageKey | string): string | undefined => {
  switch (messageKey) {
    case 'summary:complianceDeclineSystemError':
      return translations.t('summary:complianceDeclineSystemError')
    default:
      return undefined
  }
}

export const useConfirmCheckoutFinaliser = ({
  t,
  isTrusted,
  oAuthAuthorization,
  shouldUpdatePreferredPaymentDay,
  onBeforeNavigate,
  execute,
  returnRoute,
  resetCheckoutErrorOnToast = true,
  setCardScanCheckpointOnRoute = true,
}: {
  t: TFunction
  isTrusted: boolean
  oAuthAuthorization: (isTrusted: boolean) => Promise<void>
  shouldUpdatePreferredPaymentDay: boolean
  onBeforeNavigate?: () => void
  execute?: () => Promise<void>
  returnRoute?: Route
  resetCheckoutErrorOnToast?: boolean
  setCardScanCheckpointOnRoute?: boolean
}): (() => Promise<void>) => {
  const toast = useToast()
  const router = useAppRouter()
  const dispatch = useAppDispatch()

  const { started, checkout } = useCheckoutState()
  const token = useCheckoutToken()
  const hasSavedPaymentMethods = useHasSavedPaymentMethods()

  const [confirmCheckoutMutation] = useConfirmCheckoutMutation()
  const confirmArgs = useConfirmCheckoutArgs({ shouldUpdatePreferredPaymentDay })

  return async () => {
    try {
      if (typeof execute === 'function') {
        await execute()
        return
      }

      const confirmResponse = await confirmCheckoutMutation(confirmArgs).unwrap()

      await oAuthAuthorization(isTrusted)
      dispatch(setEndConfirmTime(performance.now()))

      if (confirmResponse.status !== 'FAILURE') {
        const orderAmount = started?.consumerAmount ?? checkout?.amount
        sendPurchaseEvent(token, !hasSavedPaymentMethods, orderAmount)
        dispatch(setDisplayProcessingModal(true))
      } else {
        router.push(Route.TERMINAL_ERROR)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      const maybePortalError = e?.data ?? e
      if (isPortalApiError(maybePortalError)) {
        const { route, messageKey } = getConfirmCheckoutErrorHandling({
          errorCode: maybePortalError.errorCode,
          returnRoute: returnRoute ?? Route.ENTER_PASSWORD,
        })

        if (setCardScanCheckpointOnRoute && Route.CARD_SCAN === route) {
          dispatch(setCardScanCheckpoint(getCheckpoint(maybePortalError.message)))
        }

        const toastMessage = getConfirmCheckoutToastMessage({ t }, messageKey)

        if (typeof toastMessage === 'string') {
          toast({
            message: toastMessage,
            kind: 'Failure',
          })
          if (resetCheckoutErrorOnToast) {
            dispatch(resetCheckoutError())
          }
        }

        if (route !== undefined) {
          dispatch(setDisplayProcessingModal(false))
          onBeforeNavigate?.()
          router.push(route ?? Route.TERMINAL_ERROR)
        }
      } else {
        capture.warning('Unexpected error during payment confirmation', { ...e })
      }
    }
  }
}
